import { Box, Grid, styled } from '@mui/material';
import { FieldWrapper, ThemeTypography } from 'DesignSystem';
import TextField from 'DesignSystem/Formik/TextField/TextField';
import ComponentSelection from 'FeatureModules/ComponentChain/Components/Forms/ComponentSelection';
import { IChainActivity } from 'FeatureModules/ComponentChain/types/chain.types';
import { IComponentItem } from 'FeatureModules/ComponentChain/types/component.types';
import { ESiteType } from 'FeatureModules/Site/types/site.types';
import { Field, useField } from 'formik';
import React, { FC, useMemo } from 'react';

export const ShadowedFieldBlock = styled(Grid)<{ 'grid-gap'?: number }>(
  ({ theme, 'grid-gap': gridGap = 0 }) => ({
    background: '#fff',
    padding: theme.spacing(2),
    borderRadius: 6,
    boxShadow: theme.custom.shadows[5],
    gridGap: theme.spacing(gridGap),
  })
);

interface IOutputComponentBlockProps {
  /**
   * The mode of the component block, either "select" the output component which will query all available components or
   * "translate" which will only allow the user to change the language of already selected output component, if none is selected the field will be hidden
   * Default: "select"
   */
  mode?: 'default' | 'translate';
}

const OutputComponentBlock: FC<IOutputComponentBlockProps> = ({ mode = 'default' }) => {
  const [{ value: selectedPartner }] = useField<IChainActivity['activity']['ownedBy'] | null>(
    'activity.ownedBy'
  );
  const [{ value: selectedSite }] = useField<IChainActivity['activity']['site'] | null>(
    'activity.site'
  );
  const [field, , helper] = useField<IChainActivity['activity']['component'] | null>(
    'activity.component'
  );

  const handleSelect = (component?: IComponentItem | null) => {
    helper.setValue(
      component
        ? {
            id: component.id,
            title: component.title,
            rawMaterials: component.rawMaterials,
            // We need to add the typename so that the formik validation works and initial value matches the current form values exactly
            // @ts-ignore
            __typename: 'Component',
          }
        : null
    );
  };

  const filterByRawMaterialIds = useMemo(() => {
    if (selectedSite?.siteType === ESiteType.FARM) {
      // use Set to ensure unique raw material ids in case the main raw material is also included in the additional raw materials
      return Array.from(
        new Set([
          selectedSite.rawMaterial.id,
          ...(selectedSite.additionalRawMaterialIds ? selectedSite.additionalRawMaterialIds : []),
        ])
      );
    }
    if (selectedSite?.siteType === ESiteType.CLUSTER && selectedSite.sitesRawMaterial) {
      return [selectedSite.sitesRawMaterial.id];
    }
    return undefined;
  }, [selectedSite]);

  // If the mode is "translate" and the field value is empty, hide the field
  if (mode === 'translate' && !field.value) {
    return <></>;
  }

  return (
    <ShadowedFieldBlock container>
      <Grid item xs={12}>
        <FieldWrapper
          label="Output component"
          tooltip={{
            helperText:
              'Select the component that outputs from this activity in the supply chain. Components are unique ingredients, packaging or something else that makes up your end product.',
            variant: 'INFO',
          }}
        >
          {mode === 'default' && (
            <>
              <ComponentSelection
                defaultValue={field.value || undefined}
                filterByOwnedById={selectedPartner?.id}
                filterByRawMaterialIds={filterByRawMaterialIds}
                onSelectionChange={handleSelect}
              />
            </>
          )}

          {mode === 'translate' && (
            <>
              <Field
                component={TextField}
                fullWidth
                type="text"
                name="activity.component.title"
                variant="outlined"
                size="small"
                placeholder="Enter the translation of the output component."
                data-testid="output-component-lang-input"
                inputProps={{
                  style: { fontSize: 14, height: 19 }, // Fix size difference between select and text input
                  'data-testId': 'output-component-lang-input',
                }}
              />
              <Box mb={2} />
              <ThemeTypography variant="BODY_MEDIUM" color="ALMOST_BLACK">
                You can only correct the language translation here. The output component can be
                changed in the supply chain mapping tool.
              </ThemeTypography>
            </>
          )}
        </FieldWrapper>
      </Grid>
    </ShadowedFieldBlock>
  );
};

export default OutputComponentBlock;
